import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Calendar,
  Target,
  Plus,
  User,
  MoreVertical,
  PenLine,
  Rocket,
} from 'lucide-react'
import { Spinner } from 'react-bootstrap'

import { NotFoundContent } from '../not-found-content'
import { CampaignDetailOverviewTab } from 'features/campaigns/CampaignDetailOverviewTab'
import { CampaignDetailTargetsTab } from 'features/campaigns/CampaignDetailTargetsTab'
import { CampaignDetailContactsTab } from 'features/campaigns/CampaignDetailContactsTab'
import {
  useGetCampaignCompanies,
  useGetCampaigns,
} from 'features/campaigns/campaigns_api'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import { useGetCampaignSettings } from 'features/campaigns/campaign_settings_api'
import { cn } from 'components/UI/cn'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import { useEntityBreadcrumbs, useHideFooter } from 'context/layout'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTitleRow,
  FBPageTitle,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  getCampaignTableKey,
} from 'constants/tableQueryKeys'
import { EditCampaignModalV2 } from 'features/campaigns/EditCampaignModalV2'
import apiService from 'services/api'

type DetailTab = 'overview' | 'targets' | 'contacts' | 'contact_requests'

export function CampaignDetailPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<DetailTab>('overview')
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const api = apiService()
  const { data: campaigns, isLoading } = useGetCampaigns()
  const campaignId = parseInt(id ?? '-1')
  const campaign = campaigns?.results.find((c) => c.id === campaignId)
  const { data: sequence } = useGetCampaignSequence(campaignId)
  const { data: settings } = useGetCampaignSettings(campaignId)
  const { data: campaignCompanies } = useGetCampaignCompanies(campaignId)
  const { data: contactsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contacts-count'],
    queryFn: () =>
      api.getContactsList({
        campaign: campaignId,
        campaign_page: true,
        type: 'all',
        limit: 1,
        page: 1,
      }),
    enabled: !!campaign,
    staleTime: 1000 * 60 * 5,
  })
  const breadcrumbs = useMemo(
    () => (campaign ? [{ label: campaign.name }] : []),
    [campaign]
  )
  useEntityBreadcrumbs(breadcrumbs)
  useHideFooter()

  const hasContactRequests = useMemo(() => {
    if (!campaign) return false
    return ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
  }, [campaign])
  const { data: contactRequestsData } = useQuery({
    queryKey: ['campaign', campaignId, 'contact-requests-count'],
    queryFn: async () =>
      api.fetchContactRequestJobs({ campaign: campaignId, limit: 1, page: 1 }),
    enabled: !!campaign && hasContactRequests,
    staleTime: 1000 * 60 * 5,
  })

  const targetCount = campaignCompanies?.total_count ?? 0
  const contactCount = contactsData?.count ?? 0
  const totalContactRequestsCount = contactRequestsData?.count ?? 0

  const tabsWithCounts: {
    key: DetailTab
    title: string | React.JSX.Element
  }[] = useMemo(() => {
    const tabs: {
      key: DetailTab
      title: string | React.JSX.Element
    }[] = [
      { key: 'overview', title: 'Overview' },
      {
        key: 'targets',
        title: (
          <span>
            Targets&nbsp;
            <i className="font-extralight not-italic">({targetCount})</i>
          </span>
        ),
      },
      {
        key: 'contacts',
        title: (
          <span>
            Contacts&nbsp;
            <i className="font-extralight not-italic">({contactCount})</i>
          </span>
        ),
      },
    ]
    if (hasContactRequests) {
      tabs.push({
        key: 'contact_requests',
        title: (
          <span>
            Contact Requests&nbsp;
            <i className="font-extralight not-italic">
              ({totalContactRequestsCount})
            </i>
          </span>
        ),
      })
    }
    return tabs
  }, [targetCount, contactCount, hasContactRequests, totalContactRequestsCount])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner animation="border" />
      </div>
    )
  }
  if (!campaign) {
    return <NotFoundContent />
  }

  const formattedLaunchDate = campaign.launch_date
    ? new Date(campaign.launch_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not set'
  const hasTargets = targetCount > 0
  const hasContacts = contactCount > 0
  const hasSequence = !!sequence
  const hasLaunchDate = !!campaign.launch_date
  const sequenceButtonLabel = !hasSequence
    ? 'Create Sequence'
    : hasContacts && hasLaunchDate
      ? 'Preview & Launch'
      : 'Review & Launch'
  const SequenceButtonIcon = !hasSequence
    ? Plus
    : hasContacts && hasLaunchDate
      ? Rocket
      : PenLine
  const campaignStatus = settings?.status ?? 'draft'
  const statusConfig: Record<string, { label: string; className: string }> = {
    draft: {
      label: 'Draft',
      className: 'bg-surface-alt text-fg-secondary border-line',
    },
    active: {
      label: 'Active',
      className: 'bg-green-50 text-green-700 border-green-200',
    },
    launching: {
      label: 'Launching',
      className: 'bg-green-50 text-green-700 border-green-200',
    },
    queued: {
      label: 'Queued',
      className: 'bg-green-50 text-green-700 border-green-200',
    },
    paused: {
      label: 'Paused',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    completed: {
      label: 'Completed',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    stopped: {
      label: 'Stopped',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    failed: {
      label: 'Failed',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    archived: {
      label: 'Archived',
      className: 'bg-gray-100 text-gray-700 border-gray-200',
    },
    'pending deletion': {
      label: 'Pending Deletion',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    deleted: {
      label: 'Deleted',
      className: 'bg-gray-100 text-gray-700 border-gray-200',
    },
  }
  const badge = statusConfig[campaignStatus] ?? statusConfig.draft

  return (
    <>
      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow className="w-auto flex-col !items-start gap-1">
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize',
                badge.className
              )}
            >
              {badge.label}
            </span>
            <FBPageTitle>{campaign.name}</FBPageTitle>
          </FBPageTitleRow>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/campaigns/${id}/sequence`)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-brand bg-brand px-3 py-1.5 text-sm text-white transition-colors hover:bg-brand-hover"
            >
              <SequenceButtonIcon className="size-3.5" />
              <span>{sequenceButtonLabel}</span>
            </button>
            <div className="relative">
              <button
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                className="inline-flex size-9 items-center justify-center rounded-lg border border-line text-fg-secondary transition-colors hover:bg-surface-hover"
              >
                <MoreVertical className="size-4" />
              </button>
              {showOverflowMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOverflowMenu(false)}
                  />
                  <div className="absolute right-0 top-full z-20 mt-1.5 w-48 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false)
                        setShowEditModal(true)
                      }}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
                    >
                      <PenLine className="size-4 text-fg-secondary" />
                      Edit Campaign
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </FBPageHeader>
        <div className="flex items-center gap-6 mb-4">
          <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
            <Calendar className="size-4 text-brand" />
            Launch:{' '}
            <span className="font-medium text-fg">{formattedLaunchDate}</span>
          </span>
          {hasTargets && (
            <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <Target className="size-4 text-brand" />
              Targets:{' '}
              <span className="font-medium text-fg">{targetCount}</span>
            </span>
          )}
          {campaign.owner_name && (
            <span className="flex items-center gap-1.5 text-sm text-fg-secondary">
              <User className="size-4 text-brand" />
              Owner:{' '}
              <span className="font-medium text-fg">{campaign.owner_name}</span>
            </span>
          )}
        </div>
        <FBPageInnerTabsContainer>
          <Tabs
            tabs={tabsWithCounts}
            selectedTab={activeTab}
            setSelectedTab={setActiveTab}
            tabStyle={{ width: '120px' }}
          />
        </FBPageInnerTabsContainer>
        <div className="flex flex-col flex-1 min-h-0">
          {activeTab === 'overview' && (
            <CampaignDetailOverviewTab
              campaign={campaign}
              campaignId={id!}
              hasSequence={hasSequence}
            />
          )}
          {activeTab === 'targets' && (
            <CampaignDetailTargetsTab campaign={campaign} />
          )}
          {activeTab === 'contacts' && (
            <CampaignDetailContactsTab
              campaign={campaign}
              onSwitchToTargets={() => setActiveTab('targets')}
            />
          )}
          {activeTab === 'contact_requests' && (
            <FBPageTableContainer
              style={{ height: 'calc(var(--base-page-table-height) - 102px)' }}
            >
              <ContactRequestsTable
                tableKey={getCampaignTableKey(
                  CONTACT_REQUEST_JOB_TABLE_KEY,
                  campaign.id
                )}
                campaignId={campaign.id}
              />
            </FBPageTableContainer>
          )}
        </div>
      </FBPage>
      <EditCampaignModalV2
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        campaign={campaign}
        campaignId={campaign.id}
      />
    </>
  )
}
